import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, CalendarCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SALON, NAV_ITEMS } from "@/data/content";

const LogoMark = () => (
  <svg className="w-7 h-7 text-[#B08D57]" viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 21V11C12 11 16 11 18 13C20 15 20 17 18 19C16 21 12 21 12 21Z" stroke="currentColor" strokeWidth="1.5" />
    <path d="M16 16L21 21" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="bg-[#FBF8F3]/70 backdrop-blur-xl text-[#24211D] border-b border-[#24211D]/10 sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-brand-link" aria-label="Haarstudio Nes – Startseite">
          <LogoMark />
          <span className="flex flex-col">
            <span className="font-display text-base sm:text-xl md:text-2xl tracking-wide uppercase whitespace-nowrap">Haarstudio Nes</span>
            <span className="text-[8px] sm:text-[10px] tracking-[0.2em] text-[#B08D57] uppercase font-sans whitespace-nowrap">{SALON.tagline}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5" aria-label="Hauptnavigation">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.14em] font-sans transition-colors duration-200 ${
                  isActive ? "text-[#B08D57]" : "text-[#24211D]/70 hover:text-[#24211D]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href={SALON.phoneHref}
            data-testid="header-phone-link"
            className="hidden xl:inline-block text-xs uppercase tracking-widest text-[#24211D]/70 hover:text-[#24211D] font-sans transition-colors duration-200"
          >
            {SALON.phoneDisplay}
          </a>
          <Link
            to="/kontakt#termin"
            data-testid="header-booking-btn"
            className="hidden md:inline-block border border-[#B08D57] text-[#B08D57] hover:bg-[#B08D57] hover:text-[#FBF8F3] px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] font-sans transition-colors duration-300"
          >
            Termin buchen
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="lg:hidden p-2 -mr-2 flex items-center gap-1.5" aria-label="Menü öffnen">
                <span className="text-[11px] uppercase tracking-[0.15em] font-sans text-[#24211D]/70">Menü</span>
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#FBF8F3] border-l border-[#24211D]/10 w-72">
              <nav className="flex flex-col gap-5 mt-10" aria-label="Mobile Navigation">
                {NAV_ITEMS.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `font-display text-2xl ${isActive ? "text-[#B08D57]" : "text-[#24211D]"}`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <a href={SALON.phoneHref} data-testid="mobile-menu-phone-link" className="mt-4 text-sm font-sans text-[#24211D]/70">
                  {SALON.phoneDisplay}
                </a>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#24211D] text-[#FBF8F3] py-16">
    <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-10">
      <div>
        <span className="font-display text-2xl tracking-wide uppercase block mb-2">Haarstudio Nes</span>
        <p className="text-xs text-[#FBF8F3]/70 leading-relaxed font-sans mb-3">
          Persönliche Haarkunst in entspannter Atmosphäre – mitten in Glessen.
        </p>
        <span className="text-[11px] text-[#FBF8F3]/50 block font-sans" data-testid="footer-email-placeholder">
          E-Mail: <a href={SALON.emailHref} className="hover:text-[#FBF8F3] underline-offset-4 hover:underline">{SALON.emailPlaceholder}</a>
        </span>
        <span className="text-[11px] text-[#FBF8F3]/50 block font-sans mt-1" data-testid="footer-instagram-placeholder">
          Instagram: <a href={SALON.instagramUrl} target="_blank" rel="noopener noreferrer" className="hover:text-[#FBF8F3] underline-offset-4 hover:underline">{SALON.instagramHandle}</a>
        </span>
      </div>
      <div>
        <span className="text-xs uppercase tracking-[0.18em] text-[#B08D57] block mb-3 font-sans">Standort & Kontakt</span>
        <p className="text-xs text-[#FBF8F3]/80 leading-relaxed font-sans" data-testid="footer-address">
          {SALON.street}<br />{SALON.city}<br />
          Telefon: <a href={SALON.phoneHref} className="hover:text-[#FBF8F3] underline-offset-4 hover:underline">{SALON.phoneDisplay}</a>
        </p>
      </div>
      <div>
        <span className="text-xs uppercase tracking-[0.18em] text-[#B08D57] block mb-3 font-sans">Öffnungszeiten</span>
        <p className="text-xs text-[#FBF8F3]/80 leading-relaxed font-sans" data-testid="footer-hours">
          {SALON.hours.map((h) => (
            <span key={h.days} className="block">{h.days}: {h.time}</span>
          ))}
        </p>
      </div>
      <div>
        <span className="text-xs uppercase tracking-[0.18em] text-[#B08D57] block mb-3 font-sans">Rechtliches</span>
        <ul className="text-xs text-[#FBF8F3]/70 space-y-1.5 font-sans">
          <li><Link to="/impressum" data-testid="footer-impressum-link" className="hover:text-[#FBF8F3] transition-colors duration-200">Impressum</Link></li>
          <li><Link to="/datenschutz" data-testid="footer-datenschutz-link" className="hover:text-[#FBF8F3] transition-colors duration-200">Datenschutzerklärung</Link></li>
          <li><Link to="/anfragen" data-testid="footer-studio-link" className="hover:text-[#FBF8F3] transition-colors duration-200">Studio-Login</Link></li>
        </ul>
      </div>
    </div>
  </footer>
);

const MobileActionBar = () => (
  <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-[#FBF8F3]/95 backdrop-blur-sm border-t border-[#24211D]/10 grid grid-cols-2" data-testid="mobile-action-bar">
    <a href={SALON.phoneHref} data-testid="mobile-call-btn" className="flex items-center justify-center gap-2 py-4 text-[11px] uppercase tracking-[0.14em] font-sans text-[#24211D]">
      <Phone className="w-4 h-4 text-[#B08D57]" /> Anrufen
    </a>
    <Link to="/kontakt#termin" data-testid="mobile-booking-btn" className="flex items-center justify-center gap-2 py-4 text-[11px] uppercase tracking-[0.14em] font-sans bg-[#B08D57] text-[#FBF8F3]">
      <CalendarCheck className="w-4 h-4" /> Termin buchen
    </Link>
  </div>
);

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("nes-consent")) setVisible(true);
  }, []);
  const choose = (value) => {
    localStorage.setItem("nes-consent", value);
    setVisible(false);
  };
  if (!visible) return null;
  return (
    <div className="fixed bottom-14 md:bottom-4 inset-x-3 md:inset-x-auto md:right-4 md:max-w-md z-50 bg-[#FFFFFF] border border-[#24211D]/15 p-5" data-testid="cookie-banner">
      <p className="text-xs text-[#24211D]/80 leading-relaxed font-sans mb-4">
        Wir nutzen nur technisch notwendige Cookies. Externe Inhalte (z. B. Google Maps) laden wir erst nach Ihrer Zustimmung.
        Details in der <Link to="/datenschutz" className="underline underline-offset-4">Datenschutzerklärung</Link>.
      </p>
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => choose("essential")}
          data-testid="cookie-essential-btn"
          className="border border-[#24211D]/30 text-[#24211D] px-4 py-2 text-[11px] uppercase tracking-[0.14em] font-sans hover:border-[#24211D] transition-colors duration-200"
        >
          Nur notwendige
        </button>
        <button
          onClick={() => choose("all")}
          data-testid="cookie-accept-all-btn"
          className="bg-[#B08D57] hover:bg-[#967442] text-[#FBF8F3] px-4 py-2 text-[11px] uppercase tracking-[0.14em] font-sans transition-colors duration-200"
        >
          Alle akzeptieren
        </button>
      </div>
    </div>
  );
};

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

export const Layout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <ScrollManager />
    <Header />
    <main className="flex-1 pb-16 md:pb-0">{children}</main>
    <Footer />
    <MobileActionBar />
    <CookieBanner />
  </div>
);
